import React, { useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AssetPreviewModal = ({ asset, onClose, onDownload }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  if (!asset) return null;

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  const handleLinkClick = () => {
    if (asset?.assetType === 'link' && asset?.linkUrl) {
      window.open(asset?.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getDomainIcon = (domain) => {
    if (!domain) return 'Link';
    const lowerDomain = domain?.toLowerCase();
    
    if (lowerDomain?.includes('youtube.com') || lowerDomain?.includes('youtu.be')) {
      return 'Youtube';
    }
    if (lowerDomain?.includes('twitter.com') || lowerDomain?.includes('x.com')) {
      return 'Twitter';
    }
    if (lowerDomain?.includes('facebook.com') || lowerDomain?.includes('fb.com')) {
      return 'Facebook';
    }
    if (lowerDomain?.includes('instagram.com')) {
      return 'Instagram';
    }
    if (lowerDomain?.includes('linkedin.com')) {
      return 'Linkedin';
    }
    if (lowerDomain?.includes('github.com')) {
      return 'Github';
    }
    
    return 'Link';
  };

  const getDomainColor = (domain) => {
    if (!domain) return 'text-orange-600';
    const lowerDomain = domain?.toLowerCase();
    
    if (lowerDomain?.includes('youtube.com') || lowerDomain?.includes('youtu.be')) {
      return 'text-red-600';
    }
    if (lowerDomain?.includes('twitter.com') || lowerDomain?.includes('x.com')) {
      return 'text-blue-500';
    }
    if (lowerDomain?.includes('facebook.com') || lowerDomain?.includes('fb.com')) {
      return 'text-blue-600';
    }
    if (lowerDomain?.includes('instagram.com')) {
      return 'text-pink-600';
    }
    if (lowerDomain?.includes('linkedin.com')) {
      return 'text-blue-700';
    }
    if (lowerDomain?.includes('github.com')) {
      return 'text-gray-800';
    }
    
    return 'text-orange-600';
  };

  return (
    <div
      className="fixed inset-0 bg-background/95 z-[300] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-card border border-border rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex-1 min-w-0 mr-4">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-1 line-clamp-1">
              {asset?.title}
            </h2>
            {asset?.assetType === 'link' && asset?.linkDomain && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Icon name="Globe" size={14} />
                <span>{asset?.linkDomain}</span>
              </div>
            )}
            {asset?.assetType !== 'link' && asset?.campaign && (
              <p className="text-sm text-muted-foreground line-clamp-1">{asset?.campaign}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            className="flex-shrink-0"
          />
        </div>

        <div className="flex-1 overflow-auto p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            {/* Preview Image/Link */}
            <div className="bg-muted rounded-lg overflow-hidden mb-6">
              {asset?.assetType === 'link' ? (
                <div className="relative aspect-video flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/50 p-8">
                  <div className={`mb-6 ${getDomainColor(asset?.linkDomain)}`}>
                    <Icon name={getDomainIcon(asset?.linkDomain)} size={96} />
                  </div>
                  <div className="text-center max-w-2xl">
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {asset?.linkPreviewTitle || asset?.title}
                    </h3>
                    {asset?.linkPreviewDescription && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                        {asset?.linkPreviewDescription}
                      </p>
                    )}
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Link" size={16} />
                      <span className="font-medium">{asset?.linkDomain || 'External Link'}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <Image
                  src={asset?.fullImage || asset?.thumbnail}
                  alt={asset?.fullImageAlt || asset?.thumbnailAlt}
                  className="w-full h-auto"
                />
              )}
            </div>

            {/* Asset Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
              <div className="space-y-3">
                {asset?.assetType === 'link' && asset?.linkUrl && (
                  <div className="flex items-start gap-3">
                    <Icon name="Link" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">URL</p>
                      <a 
                        href={asset?.linkUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline break-all"
                      >
                        {asset?.linkUrl}
                      </a>
                    </div>
                  </div>
                )}

                {asset?.dimensions && (
                  <div className="flex items-start gap-3">
                    <Icon name="Maximize2" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Dimensions</p>
                      <p className="text-sm text-muted-foreground">{asset?.dimensions}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Icon name="FileType" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Format</p>
                    <p className="text-sm text-muted-foreground uppercase">{asset?.format}</p>
                  </div>
                </div>

                {asset?.fileSize && asset?.assetType !== 'link' && (
                  <div className="flex items-start gap-3">
                    <Icon name="HardDrive" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">File Size</p>
                      <p className="text-sm text-muted-foreground">{asset?.fileSize}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {asset?.campaign && (
                  <div className="flex items-start gap-3">
                    <Icon name="Tag" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Campaign</p>
                      <p className="text-sm text-muted-foreground">{asset?.campaign}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Icon name="Grid3x3" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Category</p>
                    <p className="text-sm text-muted-foreground capitalize">{asset?.category}</p>
                  </div>
                </div>

                {asset?.orientation && (
                  <div className="flex items-start gap-3">
                    <Icon name="Smartphone" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Orientation</p>
                      <p className="text-sm text-muted-foreground capitalize">{asset?.orientation}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {asset?.description && (
              <div className="bg-muted rounded-lg p-4 mb-6">
                <p className="text-sm text-foreground leading-relaxed">{asset?.description}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          {asset?.assetType === 'link' ? (
            <Button
              variant="default"
              onClick={handleLinkClick}
              iconName="ExternalLink"
              iconPosition="left"
            >
              Visit Link
            </Button>
          ) : (
            <Button
              variant="default"
              onClick={() => onDownload(asset)}
              iconName="Download"
              iconPosition="left"
            >
              Download Asset
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetPreviewModal;